import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import MUICssBaseline from "@mui/material/CssBaseline";
import MUICreateTheme from "@mui/material/styles/createTheme";
import MUIThemeProvider from "@mui/material/styles/ThemeProvider";
import MUIRedColor from "@mui/material/colors/red";
import MUIGreenColor from "@mui/material/colors/green";
import MUIBlueColor from "@mui/material/colors/blue";
import MUIGreyColor from "@mui/material/colors/grey";
import Setting from "../Utility/Setting";

const salesPilotThemeDefinitionsByMode = {
    light: {
        palette: {
            background: {
                default: "#e6eef8",
                hover: "#e8ecf2",
                paper: "#ffffff",
            },
            primary: {
                main: "#1e4670",
            },
            secondary: {
                main: "#5c9acd",
            },
            accent: {
                main: "#bad3ee",
            },
            dataGrid: {
                row: {
                    oddBackground: MUIGreyColor[200],
                    oddHoverBackground: MUIGreyColor[300],
                    evenBackground: MUIGreyColor[50],
                    evenHoverBackground: MUIGreyColor[300],
                },
                outOfBoundsRow: {
                    oddBackground: MUIRedColor[100],
                    oddHoverBackground: MUIRedColor[300],
                    evenBackground: MUIRedColor[200],
                    evenHoverBackground: MUIRedColor[300],
                },
                searchNoResults: {
                    background: MUIRedColor[100],
                    border: MUIRedColor[400],
                },
                icon: {
                    priority: {
                        standard: MUIRedColor[500],
                        onOutOfBoundsRow: MUIRedColor[500],
                    },
                    calendar: {
                        standard: MUIRedColor[500],
                        onOutOfBoundsRow: MUIRedColor[500],
                    },
                    information: {
                        standard: MUIBlueColor[500],
                        onOutOfBoundsRow: MUIBlueColor[500],
                    },
                    success: {
                        standard: MUIGreenColor[700],
                        onOutOfBoundsRow: MUIGreenColor[700],
                    },
                },
                pager: {
                    text: MUIGreyColor[900],
                },
            },
        },
    },

    dark: {
        palette: {
            background: {
                default: "#0f0f0f",
                hover: "#1A1A1A",
                paper: "#121212",
            },
            primary: {
                main: "#f0e1b9",
            },
            secondary: {
                main: "#5c9acd",
            },
            accent: {
                main: "#cddd78",
            },
            dataGrid: {
                row: {
                    oddBackground: MUIGreyColor[900],
                    oddHoverBackground: MUIGreyColor[700],
                    evenBackground: MUIGreyColor[800],
                    evenHoverBackground: MUIGreyColor[700],
                },
                outOfBoundsRow: {
                    oddBackground: MUIRedColor[800],
                    oddHoverBackground: MUIRedColor[500],
                    evenBackground: MUIRedColor[600],
                    evenHoverBackground: MUIRedColor[500],
                },
                searchNoResults: {
                    background: MUIRedColor[500],
                    border: MUIRedColor[100],
                },
                icon: {
                    priority: {
                        standard: MUIRedColor[500],
                        onOutOfBoundsRow: MUIRedColor[100],
                    },
                    calendar: {
                        standard: MUIRedColor[500],
                        onOutOfBoundsRow: MUIRedColor[200],
                    },
                    information: {
                        standard: MUIBlueColor[500],
                        onOutOfBoundsRow: MUIBlueColor[200],
                    },
                    success: {
                        standard: MUIGreenColor[700],
                        onOutOfBoundsRow: MUIGreenColor[300],
                    },
                },
                pager: {
                    text: MUIGreyColor[100],
                },
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: "#1e4670",
                        backgroundImage: "none",
                    },
                },
            },
        },
    },
};

/**
 * Creates a Material UI theme object for Sales Pilot for the given mode, including
 * palette tokens and per-mode component overrides.
 * @param {string} themeMode - Theme mode, either "light" or "dark".
 * @returns {object} Material UI theme object.
 */
function createSalesPilotThemeForMode(themeMode) {
    const themeDefinition = salesPilotThemeDefinitionsByMode[themeMode] || salesPilotThemeDefinitionsByMode.light;

    return MUICreateTheme({
        ...themeDefinition,
        palette: { ...themeDefinition.palette, mode: themeMode },
        components: { ...themeDefinition.components },
    });
}

/**
 * Provides the Sales Pilot Material UI theme for the entire application and
 * re-creates the theme when the user changes light/dark mode in settings.
 * @param {object} props
 * @param {React.ReactNode} props.children - Application tree to render inside the theme.
 * @returns {React.ReactElement}
 */
export default function ThemeProvider({ children }) {
    const [themeMode, setThemeMode] = useState(Setting.get("ui.theme") || "light");
    const currentLocation = useLocation();

    useEffect(() => {
        const handleThemeSettingChange = () => {
            setThemeMode(Setting.get("ui.theme") || "light");
        };

        window.addEventListener("setting.ui.theme", handleThemeSettingChange);
        return () => window.removeEventListener("setting.ui.theme", handleThemeSettingChange);
    }, []);


    const effectiveThemeMode = currentLocation.pathname.startsWith("/patientInformation") ||
        currentLocation.pathname.startsWith("/reports/home") ||
        currentLocation.pathname.startsWith("/dailyOrderCounts") ||
        currentLocation.pathname.startsWith("/productType/new") ||
        currentLocation.pathname.startsWith("/stateSalesTax") ||
        currentLocation.pathname.startsWith("/account")
        ?
        "light" : themeMode;

    const theme = useMemo(() => createSalesPilotThemeForMode(effectiveThemeMode), [effectiveThemeMode]);

    return (
        <MUIThemeProvider theme={theme}>
            <MUICssBaseline />
            {children}
        </MUIThemeProvider>
    );
}

ThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
